import type { TransportAdapter } from './adapters';
import { TransportError, isNonRetriableStatus } from './TransportError';

interface QueueItem {
  path: string;
  data: Record<string, unknown>;
  attempts: number;
  nextAttemptAt: number;
  enqueuedAt: number;
}

export interface DeliveryQueueOptions {
  debug?: boolean;
  maxAttempts?: number;
  baseBackoffMs?: number;
  maxBackoffMs?: number;
  /**
   * Maximum age (ms) for persisted queue items before they are dropped on load
   * or next process tick. Defaults to 48 hours.
   */
  maxItemAgeMs?: number;
  loadPersisted?: () => QueueItem[];
  savePersisted?: (items: QueueItem[]) => void;
  onError?: (error: unknown, event?: Record<string, unknown>) => void;
}

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_BASE_BACKOFF_MS = 300;
const DEFAULT_MAX_BACKOFF_MS = 15000;
const DEFAULT_MAX_ITEM_AGE_MS = 48 * 60 * 60 * 1000;

export class DeliveryQueue {
  private readonly queue: QueueItem[] = [];
  private flushing = false;
  private sent = 0;
  private failed = 0;
  private dropped = 0;
  private readonly maxAttempts: number;
  private readonly baseBackoffMs: number;
  private readonly maxBackoffMs: number;
  private readonly maxItemAgeMs: number;
  private readonly debug: boolean;
  private readonly loadPersisted?: () => QueueItem[];
  private readonly savePersisted?: (items: QueueItem[]) => void;
  private readonly onError?: (error: unknown, event?: Record<string, unknown>) => void;

  constructor(private readonly transport: TransportAdapter, options: DeliveryQueueOptions = {}) {
    this.maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    this.baseBackoffMs = options.baseBackoffMs ?? DEFAULT_BASE_BACKOFF_MS;
    this.maxBackoffMs = options.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS;
    this.maxItemAgeMs = options.maxItemAgeMs ?? DEFAULT_MAX_ITEM_AGE_MS;
    this.debug = options.debug ?? false;
    this.loadPersisted = options.loadPersisted;
    this.savePersisted = options.savePersisted;
    this.onError = options.onError;
    const persisted = this.loadPersisted?.() ?? [];
    if (persisted.length > 0) {
      const now = Date.now();
      for (const item of persisted) {
        const enqueuedAt = item.enqueuedAt ?? now;
        if (now - enqueuedAt > this.maxItemAgeMs) {
          this.dropped += 1;
          continue;
        }
        this.queue.push({ ...item, enqueuedAt });
      }
      this.persist();
    }
  }

  public enqueue(path: string, data: Record<string, unknown>) {
    this.queue.push({
      path,
      data,
      attempts: 0,
      nextAttemptAt: Date.now(),
      enqueuedAt: Date.now(),
    });
    this.persist();
    void this.process();
  }

  public async flush() {
    await this.process(true);
    await this.transport.flush?.();
  }

  /**
   * Best-effort synchronous drain on page unload. Uses fetch keepalive (via
   * preferBeacon) so auth headers are included.
   */
  public drainViaBeacon() {
    if (this.queue.length === 0) return;
    const items = this.queue.splice(0, this.queue.length);
    this.persist();
    for (const item of items) {
      const eventData = { ...item.data };
      delete eventData.__prefer_beacon;
      try {
        void this.transport.send(item.path, eventData, { preferBeacon: true });
      } catch {
        // ignore: browser is unloading
      }
    }
  }

  public getStats() {
    return {
      queued: this.queue.length,
      sent: this.sent,
      failed: this.failed,
      dropped: this.dropped,
    };
  }

  public clear() {
    this.queue.splice(0, this.queue.length);
    this.persist();
  }

  private persist() {
    this.savePersisted?.(this.queue);
  }

  private evictExpired() {
    if (this.queue.length === 0) return;
    const now = Date.now();
    const cutoff = now - this.maxItemAgeMs;
    let removed = 0;
    for (let i = this.queue.length - 1; i >= 0; i -= 1) {
      if (this.queue[i].enqueuedAt <= cutoff) {
        this.queue.splice(i, 1);
        this.dropped += 1;
        removed += 1;
      }
    }
    if (removed > 0) {
      this.persist();
    }
  }

  private async process(force = false): Promise<void> {
    if (this.flushing) {
      return;
    }
    this.flushing = true;
    try {
      this.evictExpired();
      while (this.queue.length > 0) {
        const item = this.queue[0];
        if (!force && item.nextAttemptAt > Date.now()) {
          break;
        }
        try {
          const eventData = { ...item.data };
          const preferBeacon = eventData.__prefer_beacon === true;
          delete eventData.__prefer_beacon;
          await this.transport.send(item.path, eventData, { preferBeacon });
          this.queue.shift();
          this.sent += 1;
          this.persist();
        } catch (error) {
          this.failed += 1;
          this.onError?.(error, item.data);

          const status = error instanceof TransportError ? error.status : undefined;
          if (isNonRetriableStatus(status)) {
            this.queue.shift();
            this.dropped += 1;
            this.persist();
            if (this.debug) {
              console.error('[Mark] Dropping event after non-retriable status', status, item.path);
            }
            continue;
          }

          item.attempts += 1;
          if (item.attempts >= this.maxAttempts) {
            this.queue.shift();
            this.dropped += 1;
            this.persist();
            if (this.debug) {
              console.error('[Mark] Dropping event after max retries', item.path, error);
            }
            continue;
          }
          const retryAfterMs = error instanceof TransportError ? error.retryAfterMs : undefined;
          let delay: number;
          if (typeof retryAfterMs === 'number') {
            delay = Math.min(this.maxBackoffMs, Math.max(0, retryAfterMs));
          } else {
            const jitter = Math.random() * this.baseBackoffMs;
            delay = Math.min(
              this.maxBackoffMs,
              this.baseBackoffMs * 2 ** (item.attempts - 1) + jitter
            );
          }
          item.nextAttemptAt = Date.now() + delay;
          this.persist();
          break;
        }
      }
    } finally {
      this.flushing = false;
    }
  }
}
